import type { ReactNode } from 'react'
import { Check, Loader2, Trash2, Undo2, X } from 'lucide-react'
import type { BoundaryDraft } from '@/map/useBoundaryDraft'
import { Button } from '@/components/ui/button'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import type { ChangeQuestion } from '@/site-visits/siteRecordChanges'

/**
 * The buttons shared by every ring on the boundary screen.
 *
 * The site outline and an area are the same draft: undo, remove a point, stop
 * adding corners, clear, discard, save. What differs is what Save writes and
 * the extra control an area needs (Delete), which is passed in.
 *
 * On a revisit a redrawn ring asks "Changed, or drawn wrong?", and the two
 * answers stand in for Save (docs/site-visits.md).
 */
export function BoundaryDraftBar({
  draft,
  hasShape,
  saveDisabled,
  pending,
  onDiscard,
  onSave,
  question = null,
  askWhen = false,
  extra,
}: {
  draft: BoundaryDraft
  hasShape: boolean
  saveDisabled: boolean
  pending: boolean
  onDiscard: () => void
  /** With the answer, when `question` asked one, and the day if asked. */
  onSave: (isCorrection?: boolean, changedOn?: string) => void
  /** What saving asks on a revisit, in place of Save; null asks nothing. */
  question?: ChangeQuestion | null
  /** Ask when it changed too: for a change made in no visit. */
  askWhen?: boolean
  extra?: ReactNode
}) {
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-11 w-11 lg:h-9 lg:w-9"
        disabled={!draft.canUndo}
        onClick={draft.undo}
        aria-label="Undo"
      >
        <Undo2 className="h-4 w-4" />
      </Button>

      {draft.selectedIndex !== null ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 text-danger lg:h-9"
          onClick={draft.removeSelectedVertex}
        >
          <Trash2 className="h-4 w-4" />
          Remove point
        </Button>
      ) : null}

      {draft.mode === 'drawing' && hasShape ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 lg:h-9"
          onClick={() => {
            draft.startEditing()
          }}
        >
          Done adding
        </Button>
      ) : null}

      {draft.mode === 'editing' && draft.vertices.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          className="h-11 lg:h-9"
          onClick={draft.clear}
        >
          Clear
        </Button>
      ) : null}

      {extra}

      <Button
        type="button"
        variant="outline"
        size="icon"
        className="ml-auto h-11 w-11 lg:h-9 lg:w-9"
        onClick={onDiscard}
        aria-label="Discard changes"
      >
        <X className="h-4 w-4" />
      </Button>

      {question === null ? (
        <Button
          type="button"
          className="h-11 px-5 lg:h-9"
          disabled={saveDisabled || pending}
          onClick={() => {
            onSave()
          }}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          Save
        </Button>
      ) : (
        <ChangeAnswers
          question={question}
          disabled={saveDisabled || pending}
          buttonClassName="h-11 lg:h-9"
          askWhen={askWhen}
          onAnswer={onSave}
        />
      )}
    </>
  )
}
